import { OutOfCoreTaskBase } from '../OutOfCoreTaskBase.js';
import { OBJECT_STRIDE } from '../../../../wgpu/main/uniforms/ObjectUniforms.js';
import { GPUSceneRenderer } from '../../../../wgpu/gpudrivenrender/GPUSceneRenderer.js';
import { UsingMultiDraw } from '../../../../wgpu/globals.js';
import { MultiDrawBatch } from '../../../../wgpu/gpudrivenrender/MultiDrawBatch.js';
import { RENDER_PRESENTED_EVENT } from '../../../../../application/EventTypes.js';
import { USE_WBOIT } from '../../../../globals.js';

/** @import { Renderer } from '../../../../wgpu/Renderer.js' */

/**
 * Returns true if we can safely assume that the fragment order will not change.
 * This allows us whole-batch optimization by RenderBundle or (if supported) multi-draw.
 */
function hasFixedOrder(batch) {
  return !batch.sortObjects || USE_WBOIT;
}

/**
 * For WebGPU: Purpose is to upload the uniforms of a RenderBatch to the GPU.
 * Once done, it also allows renderBundles for the RenderBatch.
 */
export class UniformUploadTask extends OutOfCoreTaskBase {

  /** @type {RenderBatch} */
  #bvhRenderBatch;
  /** @type {Renderer|null} */
  #renderer = null;
  /** @type {number} */
  #activeFragCount = 0;

  /**
   * @param {OutOfCoreTileManager} outOfCoreTileManager The OutOfCoreTileManager instance creating this task
   * @param {RenderBatch}          bvhRenderBatch       Note that we finally use the ConsolidatedRenderBatch, but this may not be available at construction time yet
   * @param {number} activeFragCount - Count of frags with geometries and materials.
   */
  constructor(outOfCoreTileManager, renderBatch, activeFragCount) {
    super(outOfCoreTileManager);
    this.#bvhRenderBatch = renderBatch;
    this.#activeFragCount = activeFragCount;
  }

  /**
   * @override
   * Upload RenderBatch uniforms to GPU.
   * Note: Consolidation must be available before executing this task. Otherwwise, getting the ConsolidatedRenderBatch will fail.
   * @returns {number|undefined} The memory consumed when executing the task, undefined if no consolidation is available
   */
  execute() {
    const batch = this.#bvhRenderBatch;
    // If the batch has been interrupted, we cannot perform the upload immediately, because the changes in the batch
    // would interfere with the saved state for the interrupted batch. We have to wait until the batch has completed
    // rendering.
    if (batch.hasBeenInterrupted()) {
      return (async () => {

        while (batch.hasBeenInterrupted()) {
          // Wait until the render presented event is fired.
          await new Promise((resolve) => {
            let listener = () => {
              this.outOfCoreTileManager.getViewer().api.removeEventListener(RENDER_PRESENTED_EVENT, listener);
              resolve();
            };

            this.outOfCoreTileManager.getViewer().api.addEventListener(RENDER_PRESENTED_EVENT, listener);
          });
          // Make sure a canceled task does not continue to execute
          if (this.canceled) {
            return;
          }
        }

        this.uploadUniforms();
      })();
    } else {
      // Execute the upload immediately (without a promise to avoid turning this into an async task)
      this.uploadUniforms();
    }
  }

  /**
   * Uploads the uniforms of the RenderBatch to the GPU.
   */
  uploadUniforms() {
    const batch = this.#bvhRenderBatch;

    // Before uploading, make sure that the batch sorted for efficient rendering.
    batch.sortByVertexBuffer(batch.start, batch.lastItem);

    // Save the Renderer to make sure that when we undo() this task, we will
    // free uniforms from the correct Renderer.
    this.#renderer = this.outOfCoreTileManager.getRenderer();
    this.#renderer.uploadUniforms(batch);

    // Make sure the update doesn't happen again unless actual changes occur afterwards.
    // Note that some modification callbacks on FragmentList will cause setting uniformsNeedUpdate during loading.
    // So, without resetting it here, they would be uploaded twice.
    batch.uniformsNeedUpdate = false;

    // When using WBOIT we never sort frags by depth, so we can use RenderBundles.
    if (hasFixedOrder(batch)) {

      // Block interruption of pre-optimized RenderBatches. Why this?
      //  - For optimized RenderBatches, we want to use RenderBundles to avoid the CPU effort for forEachWGPU.
      //  - RenderBundles require that all uniforms and geometry is constantly available on GPU. For optimized tiles, we can guarantee this.
      //  - This is only possible if we let the RenderBatch finish the RenderBundle recording.

      // Note that just allowInterruption = false is not sufficient,
      // because useRenderBundles would keep false if renderBatch is already complete
      batch.allowInterruption = false;
      batch.useRenderBundles = true;
      // When allowInterruption is disabled the interrupted state is not reset anymore in forEachWGPU.
      // We reset here to prevent the batch from being stuck in the interrupted state and make sure
      // it is rendered in its entirety when recording the now enabled RenderBundle.
      batch.resetInterrupted();
    }

    // Indicate that everything is ready for this RenderBatch, i.e.
    //  - fragments are all loaded (otherwise, the BvhNode for this Batch wouldn't have been initialized in the first place)
    //  - all fragment geometry is uploaded to GPU (this is true because the UploadTask always runs at the end of the UploadTasks for fragment geometry)
    batch.isComplete = true;

    // Prepare multiDraw data if enabled (currently only supported for opaque data).
    // Point geometry uses a dedicated SSBO render path that is incompatible with multi-draw
    // batching, so batches containing points are excluded and fall back to single-draw.
    if (this.#canUseMultiDraw(batch)) {
      const multiDrawReady = GPUSceneRenderer.tryInitScene(batch, this.#activeFragCount, this.#renderer);

      if (!multiDrawReady) {
        console.error('Unexpected: Creating indirectDraw data should always succeed at this point.');
      }

      // Multidraws are currently not supported by RenderBundle encoders. So, we have to disable RenderBundles if multiDraw is used.
      if (multiDrawReady && UsingMultiDraw()) {
        batch.useRenderBundles = false;
      }
    }
  }

  /**
   * Deletes RenderBatch uniforms from GPU
   */
  undo() {
    // If the task got canceled, before the renderer was assigned
    // we didn't yet upload anything and can just return
    if (!this.#renderer) {
      return;
    }

    const batch = this.#bvhRenderBatch;
    this.#renderer.freeUniforms(batch);

    // Rendering this batch will get slower again and we cannot use the render bundles anymore.
    // Why? The RenderBundles rely on the fact that uniforms and geometry are all readily available on GPU.
    // This is only true for fully optimized tiles.
    // Note that we rely on the fact here that this task comes last in the chain (i.e. after geometry upload task)
    if (hasFixedOrder(batch)) {
      batch.allowInterruption = true;
      this.#renderer.getMainPass().clearRenderBundles(batch);

      // Don't use renderBundles again until the task is executed again.
      batch.useRenderBundles = false;
    }

    if (this.#canUseMultiDraw(batch)) {
      // Clear indirectDraw buffers (no-op if there is none)
      GPUSceneRenderer.clearMultiDrawBatch(batch, this.#renderer);
    }

    this.#renderer = null;

    // Strictly speaking, the batch is still complete at this point. But we are
    // just starting to free the node, so it will soon switch to incomplete.
    batch.isComplete = false;
  }

  /**
   * @override
   * Returns the resource cost of the task
   * @returns The resource cost of the task
   */
  getResourceCost() {
    const uniformCount = this.#bvhRenderBatch.count;

    // For performance reasons, WebGPURenderer does not create own buffers per RenderBatch.
    // Instead, it uses pages of fixed size. Therefore, the actually allocated GPU memory is higher
    // than the theoretical memory consumption, becasue...
    //  - There can be fragmentation in the buffer pages
    //  - A buffer page is always fully allocated, even if only a small part of it is used
    // However, these aspects vary depending on other tiles, so we cannot use them for a consistent computation of task costs.
    // Therefore, as a trade-off, we add 10% in general to account for unused GPU space on average.
    const OverheadFactor = 1.1;
    let bytes = Math.round(uniformCount * OBJECT_STRIDE * OverheadFactor);

    // Add costs for indirectDraw resources
    const batch = this.#bvhRenderBatch;
    if (this.#canUseMultiDraw(batch)) {
      // If multiDraw is enabled, we also have to account for the multiDraw data.
      // Note that this is only used for opaque data, so we can safely use the opaque count here.
      const multiDrawDataSize = MultiDrawBatch.computeResourceCost(this.#bvhRenderBatch);
      bytes += multiDrawDataSize;
    }

    return bytes;
  }

  /**
   * Checks, whether there is enough space left in the staging buffer to execute the task this tick.
   *
   * @returns {boolean} True if the task can be executed this tick, false otherwise
   */
  canBeExecutedThisTick() {
    const needed = this.getResourceCost();
    return this.outOfCoreTileManager.getRenderer().canUpload(needed);
  }

  /**
   * Checks whether a RenderBatch can be optimized using multi-draw.
   */
  #canUseMultiDraw(batch) {
    return (
      GPUSceneRenderer.indirectDrawEnabled() &&
      this.#activeFragCount &&
      hasFixedOrder(batch) && // Resort would invalidate the multi-draw data

      // Point geometry uses a dedicated SSBO render path that is incompatible with multi-draw
      // batching, so batches containing points are excluded and fall back to single-draw.
      !GPUSceneRenderer.containsPointGeometry(batch));

  }
}